import { api } from "./api";
import type {
  CreateInvoicePayload,
  DeclineInvoicePayload,
  Invoice,
  InvoiceCounts,
  InvoiceQueryParams,
  InvoiceShort,
  UpdateInvoicePayload,
} from "../../types/InvoiceTypes";
import { PaginatedResponse } from "../../types/GlobalTypes";
import {
  TransformAction,
  transformInvoice,
  transformShortInvoice,
  transformUpdateInvoicePayload,
} from "../../utils/invoice";

export interface InvoicesResponse extends PaginatedResponse<InvoiceShort> {}

export const invoicesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getInvoices: builder.query<
      InvoicesResponse,
      InvoiceQueryParams | undefined
    >({
      query: (params?: InvoiceQueryParams) => ({
        url: `/invoices/`,
        params,
      }),
      providesTags: (res) =>
        res?.results
          ? [
              ...res?.results?.map(({ id }) => ({
                type: "Invoices" as const,
                id,
              })),
              { type: "Invoices", id: "LIST" },
            ]
          : [{ type: "Invoices", id: "LIST" }],
      transformResponse: (response: InvoicesResponse) => ({
        ...response,
        results: response.results.map((res: InvoiceShort) =>
          transformShortInvoice(res)
        ),
      }),
    }),
    getInvoicesCounts: builder.query<InvoiceCounts, void>({
      query: () => ({
        url: `/invoices/counts/`,
      }),
      providesTags: (_) => [{ type: "Invoices", id: "COUNTS" }],
    }),
    createInvoice: builder.mutation<Invoice, CreateInvoicePayload>({
      query: (invoice) => ({
        url: `/invoices/`,
        method: "POST",
        body: invoice,
      }),
      invalidatesTags: ["Invoices"],
    }),
    getInvoice: builder.query<Invoice, { id: string }>({
      query: (payload) => ({
        url: `/invoices/${payload.id}/`,
      }),
      providesTags: (_, __, res) => [{ type: "Invoices", id: res.id }],
      transformResponse: (response: Invoice) =>
        transformInvoice(response, TransformAction.DECODE),
    }),
    getInvoicePublic: builder.query<Invoice, { id: string }>({
      query: (credentials) => ({
        url: `public/invoices/${credentials.id}/`,
      }),
      transformResponse: (response: Invoice) =>
        transformInvoice(response, TransformAction.DECODE, true),
    }),
    updateInvoice: builder.mutation<Invoice, UpdateInvoicePayload>({
      query: (invoice) => {
        return {
          url: `/invoices/${invoice.id}/`,
          method: "PATCH",
          body: transformUpdateInvoicePayload(invoice),
        };
      },
      async onQueryStarted(updatedInvoice, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // Update the specific invoice in the cache
          dispatch(
            invoicesApi.util.updateQueryData(
              "getInvoice",
              { id: updatedInvoice.id },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );

          // Update the invoice in the list if it exists
          dispatch(
            invoicesApi.util.updateQueryData(
              "getInvoices",
              undefined,
              (draft) => {
                const index = draft.results.findIndex(
                  (invoice) => invoice.id === updatedInvoice.id
                );
                if (index !== -1) {
                  draft.results[index] = {
                    ...draft.results[index],
                    ...data,
                  };
                }
              }
            )
          );
        } catch (error) {
          // Handle error case if needed
        }
      },
      transformResponse: (response: Invoice) =>
        transformInvoice(response, TransformAction.DECODE),
    }),
    approveInvoice: builder.mutation<Invoice, string>({
      query: (id) => {
        return {
          url: `/invoices/${id}/approve/`,
          method: "POST",
        };
      },
      invalidatesTags: ["Invoices", "Users"],
    }),
    issueInvoice: builder.mutation<Invoice, string>({
      query: (id) => {
        return {
          url: `/invoices/${id}/issue/`,
          method: "POST",
        };
      },
      invalidatesTags: ["Invoices", "Users"],
    }),
    declineInvoice: builder.mutation<null, DeclineInvoicePayload>({
      query: ({ id, declined_reason }) => ({
        url: `/invoices/${id}/decline/`,
        method: "POST",
        body: { declined_reason },
      }),
      invalidatesTags: ["Invoices", "Users"],
    }),
    makePublic: builder.mutation<Invoice, string>({
      query: (id) => ({
        url: `/invoices/${id}/make_public/`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices"],
    }),
    duplicateInvoice: builder.mutation<Invoice, string>({
      query: (id) => ({
        url: `/invoices/${id}/duplicate/`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices"],
    }),
    removeInvoice: builder.mutation<null, string>({
      query: (id) => ({
        url: `/invoices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoices"],
    }),
    cancelInvoice: builder.mutation<null, string>({
      query: (id) => ({
        url: `/invoices/${id}/cancel/`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices", "Users"],
    }),
  }),
});

export const {
  useApproveInvoiceMutation,
  useCancelInvoiceMutation,
  useCreateInvoiceMutation,
  useDeclineInvoiceMutation,
  useDuplicateInvoiceMutation,
  useGetInvoicePublicQuery,
  useGetInvoiceQuery,
  useGetInvoicesQuery,
  useGetInvoicesCountsQuery,
  useIssueInvoiceMutation,
  useMakePublicMutation,
  useRemoveInvoiceMutation,
  useUpdateInvoiceMutation,
} = invoicesApi;
